import React, { FC, Fragment } from 'react';
import { default as formatFn } from 'date-fns/format';
import isValid from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';
import PropTypes from 'prop-types';

import { useDateFns } from '../Provider';
import parseToDateWithTimezone, {
  isoDateRegexpWithoutTimezone,
} from './parseToDateWithTimeZone';
import { DateTimeProps, FormatsObject } from './types';

const formats: FormatsObject = {
  date: {
    short: 'P',
    medium: 'PP',
    long: 'PPP',
  },
  time: {
    short: 'p',
    medium: 'pp',
    long: 'ppp',
  },
  dateTime: {
    short: 'Pp',
    medium: 'PPpp',
    long: 'PPPppp',
  },
};

const isValidDate = (value: string): boolean => {
  const date = parseISO(value);
  return isValid(date) && isoDateRegexpWithoutTimezone.test(value);
};

const formatTimezone = (timezone: number): string => {
  const sign = Math.sign(timezone) >= 0 ? '+' : '-';
  const timezoneString = String(timezone).padStart(2, '0');
  return `${sign}${timezoneString}`;
};

const DateTime: FC<DateTimeProps> = ({ value, type, pattern, format }) => {
  const dateFnsLocale = useDateFns();
  if (!isValidDate(value)) {
    return null;
  }
  const { date, timezone } = parseToDateWithTimezone(value);
  const result = formatFn(date, format ?? formats[type][pattern], {
    locale: dateFnsLocale,
  });

  return (
    <Fragment>
      {result.replace(/GMT([-+]\d+)/, `GMT${formatTimezone(timezone)}`)}
    </Fragment>
  );
};

const propValidation = (props, propName, componentName) => {
  const { type, pattern, format } = props;

  if (!((type && pattern) || format)) {
    return new Error(
      `Missing required props! You need to specify either type AND pattern, OR format in ${componentName}`
    );
  }

  return null;
};

DateTime.propTypes = {
  type: propValidation,
  pattern: propValidation,
  format: propValidation,
  value: PropTypes.string.isRequired,
};

export default DateTime;
